<template>
  <div
    v-if="selectedTreatment && doctors"
    id="treatment-detail-selector"
    class="mx-auto h-full w-full max-w-screen-md flex flex-col"
  >
    <div v-if="!customTime" class="flex justify-between items-center mb-2">
      <div class="text-h6 text-gray-800 w-3/4">
        {{ selectedTreatment.name }}
      </div>

      <v-btn
        class=""
        color="gray-50"
        icon="mdi-close"
        variant="text"
        density="compact"
        @click="handleClearTreatment"
      />
    </div>
    <v-divider></v-divider>
    <div class="grid grid-cols-2 gap-4 pt-4 mb-4">
      <div>
        <div class="admin-options-wrapper">
          <div class="text-button mb-4 pt-2">
            SELECT A PRACTITIONER
          </div>
            <div class="w-full overflow-y-scroll px-4 fixedScrollable">
              <TextCard
                v-for="admin in filteredDoctors"
                :key="`admin-${admin.uuid}`"
                class="w-full mb-2 cursor-pointer"
                :initialsColor="admin.color"
                :initials="$filters.initials(admin.name)"
                :selected="selectedAdmin === admin.uuid"
                :text="admin.name"
                @click="handleSelectItem('selectedAdmin', admin.uuid)"
              />
            </div>
        </div>
        <div class="px-4 mb-3">
          <v-btn
            variant="text"
            density="comfortable"
            class="align-center"
            color="#7f7f7f"
            @click="onShowSettings"
          >
            <span>Practitioner settings</span>

            <v-icon
              class="ml-2"
              color="gray"
            >mdi-cog</v-icon>
          </v-btn>
        </div>
      </div>
      <div class="room-options-wrapper">
        <div class="text-button mb-4 pt-2">
          SELECT A TIME
        </div>
        <div class="w-full overflow-y-scroll px-4 fixedScrollable">
          <TextCard
            v-for="(time, i) in [10, 15, 30, 45, 60]"
            :key="`time-card-${i}`"
            class="w-full mb-2 cursor-pointer"
            :selected="selectedTime === time"
            :text="`${time} Min`"
            @click="selectTime(time)"
          />
        </div>
      </div>
    </div>
    <div class="w-full flex justify-center px-2 mb-2">
      <v-btn
        class="mr-6"
        color="primary"
        :disabled="addTreatmentButtonIsDisabled"
        @click="handleAddTreatment"
      >
        ADD TREATMENT
      </v-btn>
      <v-btn
        class="ml-6"
        color="gray-lighten-2"
        @click="handleClearTreatment"
      >
        CLOSE
      </v-btn>
    </div>
  </div>
</template>

<script lang="ts">
import { boolean } from 'boolean';
import { defineComponent } from 'vue';
import { mapState } from 'vuex';
import {mdiClose, mdiCog} from '@mdi/js';
import TextCard from '@/components/cards/TextCard.vue';

declare interface BaseData {
  icons: { [key: string]: string };
  selectedTime: number;
  selectedAdmin: number | string;
  selectedRoom: number | string;
  times: number[];
}

export default defineComponent({
  name: 'TreatmentDetailSelector',
  components: { TextCard },
  data(): BaseData {
    return {
      icons: { settings: mdiCog, close: mdiClose },
      selectedTime: 0,
      selectedAdmin: '',
      selectedRoom: '',
      times: [10, 15, 30, 45, 60],
    };
  },
  computed: {
    ...mapState({
      // @ts-ignore
      rooms: (state) => state.auth.dashboard.rooms,
      // @ts-ignore
      doctors: (state) => state.doctors,
      // @ts-ignore
      appointment: (state) => state.appointment,
      // @ts-ignore
      selectedTreatment: (state) => state.selectedTreatment,
    }),
    roomOptions() {
      if (this.selectedTreatment?.rooms?.length) {
        return this.selectedTreatment.rooms;
      } else {
        return this.rooms;
      }
    },
    filteredDoctors() {
      return this.doctors
        .filter(({ is_enabled: x }) => boolean(x))
        .filter((d) =>
          d.treatments.find((t) => t.uuid === this.selectedTreatment.uuid),
        );
    },
    addTreatmentButtonIsDisabled(): boolean {
      if (this.customTime) {
        return !this.selectedTime || !this.selectedAdmin;
      } else {
        return !this.selectedAdmin;
      }
    },
    params() {
      return {
        user: this.$store.state.auth.user.uuid,
        duration: this.selectedTime * 60,
        treatment: this.selectedTreatment.uuid,
        doctor: this.selectedAdmin,
        room: this.selectedRoom,
      };
    },
  },
  mounted() {
    this.setInitialRoomSelection();
    this.selectedTime = this.selectedTreatment.duration || 0;
  },
  methods: {
    setInitialRoomSelection() {
      this.selectedRoom = this.appointment.room
        ? this.appointment.room.uuid
        : this.roomOptions[0].uuid;
    },
    selectTime(time: number): void {
      if (this.selectedTime === time) {
        this.selectedTime = 0;
      } else {
        this.selectedTime = time;
      }
    },
    handleSelectItem(property: any, id: number | string): void {
      if (property === 'selectedAdmin') {
        this.selectedAdmin = id;
      } else if (property === 'selectedRoom') {
        this.selectedRoom = id;
      }
    },
    async handleAddTreatment(): Promise<void> {
      const atPayload = {
        params: this.params,
        appointmentId: this.appointment.uuid,
      };
      await this.$store.dispatch('ADD_APPOINTMENT_TREATMENT', atPayload);
      this.$emit('back');
    },
    handleClearTreatment(): void {
      this.$emit('back');
    },
    onShowSettings() {
      this.$emit('on-show-settings');
    },
  },
});
</script>

<style scoped>
.admin-options-wrapper, .room-options-wrapper {
  @apply p-1 w-full flex flex-col items-center;
}
.fixedScrollable {
    height: 350px;
    overflow-y: scroll;
    overflow-x: hidden;
}
.fixedScrollable::-webkit-scrollbar {
    display: block;
    width: 5px;
    background-color: #eaeaea;
    border-radius: 5px;
    padding-left: 5px;
}
.fixedScrollable::-webkit-scrollbar-thumb {
    display: block;
    width: 5px;
    background-color: #4e61b1;
    border-radius: 5px;
    cursor: pointer;
}
</style>
